import React, { useState } from 'react';
import {
  AutoAwesome as CoachIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import type { JotCoachGap, JotCoachProviderQuestion } from '../store/hooks';

import { GapItem } from './GapItem';
import { ProviderQuestionItem } from './ProviderQuestionItem';

interface SectionCoachCardProps {
  sectionName: string;
  gaps: JotCoachGap[];
  providerQuestions: JotCoachProviderQuestion[];
  onApplyEdit?: (gap: JotCoachGap) => void;
  onSubmitAnswer?: (question: JotCoachProviderQuestion, answer: string) => void;
}

export const SectionCoachCard: React.FC<SectionCoachCardProps> = ({
  sectionName,
  gaps,
  providerQuestions,
  onApplyEdit,
  onSubmitAnswer,
}) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const totalGaps = gaps.length;
  const totalQuestions = providerQuestions.length;
  const highPriorityCount = gaps.filter(g => g.priority === 'High').length;

  // Determine border color based on highest priority
  const getBorderColor = () => {
    if (highPriorityCount > 0) return '#d32f2f';
    if (gaps.some(g => g.priority === 'Medium')) return '#f57c00';
    return '#f9a825';
  };

  return (
    <Card
      sx={{
        width: '100%',
        boxShadow: expanded ? 3 : 1,
        border: '2px solid',
        borderColor: getBorderColor(),
        borderRadius: 2,
        transition: 'all 0.3s ease',
        mt: 2,
        mb: 2,
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      {/* Compact Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          bgcolor: expanded ? 'primary.50' : 'background.paper',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <CoachIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
            JotCoach
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {sectionName}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          {totalGaps > 0 && (
            <Badge
              badgeContent={totalGaps}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  bgcolor: highPriorityCount > 0 ? '#d32f2f' : '#f57c00',
                  fontSize: '0.65rem',
                  height: 18,
                  minWidth: 18,
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, pr: 0.5 }}>
                Gaps
              </Typography>
            </Badge>
          )}
          {totalQuestions > 0 && (
            <Badge
              badgeContent={totalQuestions}
              color="info"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  height: 18,
                  minWidth: 18,
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, pr: 0.5 }}>
                Q's
              </Typography>
            </Badge>
          )}
        </Stack>
        <IconButton size="small">
          {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <CardContent sx={{ maxHeight: 600, overflowY: 'auto', p: 2 }}>
          {/* Gaps */}
          {totalGaps > 0 && (
            <Box sx={{ mb: totalQuestions > 0 ? 2 : 0 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'error.main' }}
              >
                Documentation Gaps ({totalGaps})
              </Typography>
              <Stack spacing={1.5}>
                {gaps.map(gap => (
                  <GapItem key={gap.id} gap={gap} onApplyEdit={onApplyEdit} />
                ))}
              </Stack>
            </Box>
          )}

          {/* Provider Questions */}
          {totalQuestions > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'info.main' }}
              >
                Provider Questions ({totalQuestions})
              </Typography>
              <Stack spacing={1.5}>
                {providerQuestions.map((question, idx) => (
                  <ProviderQuestionItem
                    key={`${question.section_id}-${idx}`}
                    question={question}
                    onSubmitAnswer={onSubmitAnswer}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};
